import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "@aws-amplify/auth";
import { Partner } from "@api";
import { SocialProvider } from "@auth";
import { getClientSecretGraphQL, useGetClientSecret } from "@queries";
import { loadStripe } from "@stripe/stripe-js";

import { BillingDetails, GiftCardOffer, PartnerStatsAll, PartnerAirdropTransaction, CreditCardPaymentMethod, Invoice, PartnerDashboardData, LSACategory, LSAGroup, TimeRangeFilter } from "../types";
import { LifestyleAnalyticsResponse } from "../types/lifestyle-analytics";
import { SignInInput, SignUpBasicInput, ResetPasswordInput } from "@auth";
import { Env } from "@config";
import { useLazyQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { getPartnerGraphQL, getPartnerPaymentMethodsGraphQL, getPartnerInvoicesGraphQL, getPartnerBillingDetailsGraphQL, getPartnerDashboardDataGraphQL } from "@queries";
import { computeCreditCardStatus } from "./utils";
interface SignUpUsername {
  user: { username: string };
}

const STRIPE_API_KEY: string = Env.STRIPE_API_KEY_CA || "";
if (STRIPE_API_KEY === "") console.log("ERROR: empty API key");
const stripePromise = loadStripe(STRIPE_API_KEY);

export interface AuthState {
  partner: Partner | null;
  cognitoUser: any;
  setCognitoUser: (user: any) => void;
  updatePartnerData: (partner: Partial<Partner>) => void;
  signIn(input: SignInInput): Promise<string>;
  signOut(): Promise<void>;
  socialSignIn(provider: SocialProvider): Promise<void>;
  syncPartner: (partner: Partner) => Promise<void>;
  newPartnerInfo: any;
  setNewPartnerInfo: (value: any) => void;
  clientSecret: string;
  updateClientSecret: (secret: string) => void;
  customerId: string | undefined;
  stripePromise: any;
  isLoadingData: boolean;
  setIsLoadingData: (isLoading: boolean) => void;
  isLoadingStartUpPartnerData: boolean;
  setIsLoadingStartUpPartnerData: (isLoading: boolean) => void;
  isSetupComplete: boolean | undefined; // this is true when all new partner setup steps are done
  calculateIsSetupComplete: () => void;
  selectedPageIndex: number | undefined;
  setSelectedPageIndex: (index: number) => void;
  isPerksPageVisited: boolean;
  setIsPerksPageVisited: (value: boolean) => void;
  billingDetails: BillingDetails,
  setBillingDetails: (value: BillingDetails) => void;
  giftCardOffers: GiftCardOffer[] | null;
  setGiftCardOffers: (value: GiftCardOffer[]) => void;
  partnerStatistics: PartnerStatsAll | undefined,
  setPartnerStatistics: (value: PartnerStatsAll) => void,
  partnerAirdropTransactions: PartnerAirdropTransaction[] | null;
  setPartnerAirdropTransactions: (value: PartnerAirdropTransaction[]) => void;
  refetchPartner: () => Promise<void>;
  partnerDashboardData: PartnerDashboardData | undefined;
  setPartnerDashboardData: (value: PartnerDashboardData) => void;
  lifestyleCategories: LSACategory[] | undefined;
  updateLifestyleCategories: (value: LSACategory[]) => void;
  lifestyleGroup: LSAGroup | undefined;
  updateLifestyleGroup: (value: LSAGroup) => void;
  lifestyleAnalytics: LifestyleAnalyticsResponse | undefined;
  updateLifestyleAnalytics: (value: LifestyleAnalyticsResponse) => void;
  lifestyleSelectedGroupId: string | undefined;
  setLifestyleSelectedGroupId: (value: string | undefined) => void;
  lifestyleSelectedTimeRange: TimeRangeFilter;
  setLifestyleSelectedTimeRange: (value: TimeRangeFilter) => void;
  creditCardStatus: "valid" | "expired" | "expiringSoon";
  setCreditCardStatus: (value: "valid" | "expired" | "expiringSoon") => void;
}

export const AuthContext = createContext<AuthState>({} as AuthState);

interface AuthProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProps> = ({ children }) => {
  const navigate = useNavigate();
  //const { getPartner, partner: partnerData } = useGetPartner();
  //const { getPartnerPaymentMethods } = useGetPartnerPaymentMethods();
  //const { getPartnerInvoices } = useGetPartnerInvoices();
  //const { getBillingDetails } = useGetBillingDetails();
  //const { getClientSecret, customerId, clientSecretData } = useGetClientSecret();
  // Note: we are not currently using the customerId on the frontend, and if required, the code may need to be updated to match how we handle/update the clientSecret

  const [selectedPageIndex, setSelectedPageIndex] = useState<number | undefined>(undefined);
  const [isPerksPageVisited, setIsPerksPageVisited] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [customerId, setCustomerId] = useState<string>("");
  const [partner, setPartner] = useState<Partner | null>(null);
  const [billingDetails, setBillingDetails] = useState<BillingDetails>
  ({
    invoices: null,
    creditCards: null,
    planAmount: null
  });
  const [cognitoUser, setCognitoUser] = useState<any>(null);
  const [newPartnerInfo, setNewPartnerInfo] = useState<Object>({});
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isLoadingStartUpPartnerData, setIsLoadingStartUpPartnerData] = useState<boolean>(true);
  const [isSetupComplete, setIsSetupComplete] = useState<boolean | undefined>(undefined);
  const [giftCardOffers, setGiftCardOffers] = useState<Array<GiftCardOffer> | null>(null);
  const [partnerAirdropTransactions, setPartnerAirdropTransactions] = useState<Array<PartnerAirdropTransaction> | null>(null);
  const [partnerDashboardData, setPartnerDashboardData] = useState<PartnerDashboardData | undefined>(undefined);
  const [lifestyleCategories, setLifestyleCategories] = useState<LSACategory[] | undefined>(undefined);
  const [lifestyleGroup, setLifestyleGroup] = useState<LSAGroup | undefined>(undefined);
  const [lifestyleAnalytics, setLifestyleAnalytics] = useState<LifestyleAnalyticsResponse | undefined>(undefined);
  const [lifestyleSelectedGroupId, setLifestyleSelectedGroupId] = useState<string | undefined>(undefined);
  const [lifestyleSelectedTimeRange, setLifestyleSelectedTimeRange] = useState<TimeRangeFilter>(TimeRangeFilter.YEAR_TO_DATE);
  const [creditCardStatus, setCreditCardStatus] = useState<"valid" | "invalid" | "expired" | "expiringSoon">("valid");
  const [partnerStatistics, setPartnerStatistics] = useState<PartnerStatsAll | undefined>({
    current: {
      date: "",
      numTeamMembers: 0,
      numActiveMembers: 0,
      totalRewardFiatValue: 0,
      numAirdrops: 0,
      popularBrands: []
    }
  });
  const computeStatus = (cards: [CreditCardPaymentMethod] | null | undefined) =>
    computeCreditCardStatus(cards as CreditCardPaymentMethod[] | null | undefined);

  const clientSecretQuery = gql(getClientSecretGraphQL);
  const [getClientSecret] = useLazyQuery<{
    getPartnerCardRegisterClientSecretAndCustomerId: {
      customerId: string;
      clientSecret: string;
    }
  }>(clientSecretQuery, {
    fetchPolicy: "network-only",
    onCompleted: async (data) => {
      console.log("client secret query complete.");
      console.log("data", data?.getPartnerCardRegisterClientSecretAndCustomerId);
      setClientSecret(data?.getPartnerCardRegisterClientSecretAndCustomerId?.clientSecret);
      setCustomerId(data?.getPartnerCardRegisterClientSecretAndCustomerId?.customerId);
    },
    onError: async (error: any) => {
      console.error(`Failed to "getClientSecret", error: ${error}...`);
    },
  });

  const partnerQuery = gql(getPartnerGraphQL);
  const paymentMethodsQuery = gql(getPartnerPaymentMethodsGraphQL);
  const [getPartner] = useLazyQuery<{
    getPartner: Partner;
  }>(partnerQuery, {
    onCompleted: async (data) => {
      console.log("partner query complete.");
      console.log("data", data?.getPartner);
      setPartner(data?.getPartner);
    },
    onError: async (error: any) => {
      console.error(`Failed to "getPartner", error: ${error}...`);
      await Auth.signOut();
      location.replace("/");
    },
  });

  const [getPartnerPaymentMethods] = useLazyQuery<{
    getPartnerPaymentMethods: [CreditCardPaymentMethod];
  }>(paymentMethodsQuery, {
    onCompleted: async (data) => {
      console.log("payment methods query complete.");
      console.log("data", data?.getPartnerPaymentMethods);
      setBillingDetails({
        invoices: billingDetails.invoices,  // temporary
        creditCards: data?.getPartnerPaymentMethods,
        planAmount: billingDetails.planAmount
      });
      setCreditCardStatus(computeStatus(data?.getPartnerPaymentMethods));
    },
    onError: async (error: any) => {
      console.error(`Failed to "getPartnerPaymentMethods", error: ${error}...`);
    },
  });

  const invoicesQuery = gql(getPartnerInvoicesGraphQL);
  const [getPartnerInvoices] = useLazyQuery<{
    getPartnerInvoices: [Invoice];
  }>(invoicesQuery, {
    onCompleted: async (data) => {
      console.log("invoices query complete.");
      console.log("data", data?.getPartnerInvoices);
      setBillingDetails({
        invoices: data?.getPartnerInvoices,
        creditCards: billingDetails.creditCards,
        planAmount: billingDetails.planAmount
      });
    },
  });

  const billingDetailsQuery = gql(getPartnerBillingDetailsGraphQL);
  const [getBillingDetails] = useLazyQuery<{
    getPartnerBillingDetails: BillingDetails;
  }>(billingDetailsQuery, {
    onCompleted: async (data) => {
      console.log("billing details query complete.");
      console.log("data", data?.getPartnerBillingDetails);
      setBillingDetails({
        invoices: billingDetails.invoices,
        creditCards: billingDetails.creditCards,
        planAmount: data?.getPartnerBillingDetails.planAmount
      });
    },
  });

  const dashboardDataQuery = gql(getPartnerDashboardDataGraphQL);
  const [getPartnerDashboardData] = useLazyQuery<{
    getPartnerDashboardData: PartnerDashboardData;
  }>(dashboardDataQuery, {
    onCompleted: async (data) => {
      console.log("dashboard data query complete.");
      console.log("data", data?.getPartnerDashboardData);
      setPartnerDashboardData(data?.getPartnerDashboardData);
    },
  });

  // save the client secret to the Context state var
  /* useEffect(() => {
    console.log("set clientSecretData");
    setClientSecret(clientSecretData);
  }, [clientSecretData]);

  useEffect(() => {
    let active = true;
    console.log("set partnerData");
    if (active && partnerData) {
      setPartner(partnerData);
    }

    return () => {
      active = false;
    };
  }, [partnerData]); */

  useEffect(() => {
    const checkIfSignedIn = async () => {
      console.log("running checkIfSignedIn...")
      setIsLoadingStartUpPartnerData(true);
      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        if (cognitoUser) {
          await Promise.all([
            getPartner(),
            getClientSecret(),
            getPartnerPaymentMethods(),
            getPartnerDashboardData(),
            //getPartnerInvoices(),
            //getBillingDetails()
          ]);
          /* if(partner && partner.status === "PartnerApproved") {
            navigate("/dashboard/home");
          } else if(partner && partner.status !== "PartnerApproved") {
            navigate("/dashboard/welcome");
          } */
          //getPartnerPaymentMethods();
          //getPartnerInvoices();
          //getBillingDetails();
          console.log("... done running checkIfSignedIn");
        }
      } catch (error) {
        console.log("error", error);
      } finally {
        setIsLoadingStartUpPartnerData(false);
      }
    };
    checkIfSignedIn();
  }, []);

  useEffect(() => {
    console.log("This is the partner", partner);
    console.log("This is the billingDetails", billingDetails);
  }, [partner, billingDetails]);

  useEffect(() => {
    setCreditCardStatus(computeStatus(billingDetails.creditCards));
  }, [billingDetails.creditCards]);

  function updatePartnerData(newPartner: Partial<Partner>) {
    console.log("updating partner data... ", newPartner);
    setPartner((currentPartner) => {
      if (!currentPartner) {
        return newPartner as Partner;
      }

      const mergedImages =
        newPartner.images === undefined
          ? currentPartner.images
          : newPartner.images === null
            ? null
            : { ...(currentPartner.images ?? {}), ...newPartner.images };

      const mergedAddress =
        newPartner.address === undefined
          ? currentPartner.address
          : newPartner.address === null
            ? null
            : { ...(currentPartner.address ?? {}), ...newPartner.address };

      const mergedPremiumSettings =
        newPartner.premiumSettings === undefined
          ? currentPartner.premiumSettings
          : newPartner.premiumSettings;

      const mergedOfferAmountsManagement =
        newPartner.offerAmountsManagement === undefined
          ? currentPartner.offerAmountsManagement
          : newPartner.offerAmountsManagement;

      const mergedLsaGroups =
        newPartner.lsaGroups === undefined ? currentPartner.lsaGroups : newPartner.lsaGroups;

      return {
        ...currentPartner,
        ...newPartner,
        images: mergedImages,
        address: mergedAddress,
        premiumSettings: mergedPremiumSettings,
        offerAmountsManagement: mergedOfferAmountsManagement,
        lsaGroups: mergedLsaGroups,
      } as Partner;
    });
  }

  const updateClientSecret = (newSecret: string): void => {
    setClientSecret(newSecret);
  };

  const updateLifestyleCategories = (newLifestyleCategories: LSACategory[]) => {
    setLifestyleCategories(newLifestyleCategories);
  }

  const updateLifestyleGroup = (newLifestyleGroup: LSAGroup) => {
    console.log("updating lifestyle group... ", newLifestyleGroup);
    setLifestyleGroup(newLifestyleGroup);
  }

  const updateLifestyleAnalytics = (newLifestyleAnalytics: LifestyleAnalyticsResponse) => {
    console.log("updating lifestyle analytics... ", newLifestyleAnalytics);
    setLifestyleAnalytics(newLifestyleAnalytics);
  };

  const refetchPartner = async () => {
    getPartner();
  }

  // TODO: why was this method a callback? (see the original below) Is it necessary?
  async function signIn({ emailAddress, password }: SignInInput): Promise<string> {
    console.log("sign in and load ...");
    const cognitoUserResult = await Auth.signIn(emailAddress, password);

    if (cognitoUserResult.challengeName === "NEW_PASSWORD_REQUIRED") {
      setCognitoUser(cognitoUserResult);
      return "NEW_PASSWORD_REQUIRED";
    } else {
      if (partner === null) {
        await Promise.all([
          getPartner(),
          getClientSecret(),
          getPartnerPaymentMethods(),
          getPartnerDashboardData(),
        ]);
      } else {
        getClientSecret();
        getPartnerPaymentMethods();
        getPartnerDashboardData();
      }
      console.log("... done sign in and load");
      return "";
    }
  }

  const socialSignIn = useCallback(async (provider: SocialProvider) => {
    console.log(`Starting federated sign in with provider ${provider}...`);
    await Auth.federatedSignIn({ provider });
    console.log("Done social signin");
    const credentials = await Auth.currentAuthenticatedUser();
    await Promise.all([
      getPartner(),
      getClientSecret(),
      getPartnerPaymentMethods(),
      getPartnerDashboardData(),
    ]);
    /* console.log(
      `Completed federated sign in with provider ${provider}, credentials: ${JSON.stringify(
        credentials,
      )}...`,
    );
    console.info("Starting fetching partner information...");
    getPartner();
    console.info("Completed fetching partner information...");
    if(partner && partner.status === "PartnerApproved") {
     navigate("/dashboard/");
    } else if(partner && partner.status !== "PartnerApproved") {
     navigate("/dashboard/welcome");
    } */
  }, []);

  const signOut = useCallback(async () => {
    try {
      sessionStorage.setItem("preloaderFlow", "signout");
    } catch (err) {
      // Ignore storage failures (private mode, browser restrictions).
    }

    await Auth.signOut();
    setPartner(null);
    location.replace("/");
  }, []);

  const syncPartner = useCallback(async (partner: Partner) => {
    console.log("syncing partner in the auth context");
    console.log("partner", partner);
    setPartner(partner);
  }, []);

  // tests all of the setup conditions, and return true if all of them are true
  const calculateIsSetupComplete = () => {
    setIsSetupComplete(partner?.premiumSettings[0]?.users === undefined || partner?.premiumSettings[0]?.users.length === 0 ? false : true);
  }

  const authState: AuthState = {
    partner,
    updatePartnerData,
    cognitoUser,
    setCognitoUser,
    signIn,
    signOut,
    socialSignIn,
    syncPartner,
    newPartnerInfo,
    setNewPartnerInfo,
    customerId,
    clientSecret,
    stripePromise,
    updateClientSecret,
    isLoadingData,
    setIsLoadingData,
    isLoadingStartUpPartnerData,
    setIsLoadingStartUpPartnerData,
    selectedPageIndex,
    setSelectedPageIndex,
    isPerksPageVisited,
    setIsPerksPageVisited,
    billingDetails,
    setBillingDetails,
    giftCardOffers,
    setGiftCardOffers,
    partnerStatistics,
    setPartnerStatistics,
    isSetupComplete,
    calculateIsSetupComplete,
    partnerAirdropTransactions,
    setPartnerAirdropTransactions,
    refetchPartner,
    partnerDashboardData,
    setPartnerDashboardData,
    lifestyleCategories,
    updateLifestyleCategories,
    lifestyleGroup,
    updateLifestyleGroup,
    lifestyleAnalytics,
    updateLifestyleAnalytics,
    lifestyleSelectedGroupId,
    setLifestyleSelectedGroupId,
    lifestyleSelectedTimeRange,
    setLifestyleSelectedTimeRange,
    creditCardStatus,
    setCreditCardStatus
  };

  return (
    <AuthContext.Provider value={authState}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the context
export const useAuth = (): AuthState => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Convenience hooks
export const useUser = (): Partner | null => {
  const { partner } = useAuth();
  return partner;
};

export const useSignIn = (): (input: SignInInput) => Promise<string> => {
  return useAuth().signIn;
};

export const useSignOut = (): () => Promise<void> => {
  return useAuth().signOut;
};

export const useSocialSignIn = (): (provider: SocialProvider) => Promise<void> => {
  return useAuth().socialSignIn;
};

export const useSyncPartner = (): (partner: Partial<Partner>) => void => {
  console.log("useSyncPartner");
  return useAuth().updatePartnerData;
};

export const useSignUp = () => {
  return async function signUp({
    emailAddress,
    firstName,
    lastName,
    password,
    //customDescription,
    //customOrganization,
    //customPhoneNumber,
    //customWebsite,
  }: SignUpBasicInput): Promise<string> {
    const signUpResult = await Auth.signUp({
      username: emailAddress.trim(),
      password: password.trim(),
      attributes: {
        family_name: lastName.trim(),
        given_name: firstName.trim(),
        //phone_number: customPhoneNumber,
        //"custom:description": customDescription,
        //"custom:organization": customOrganization,
        //"custom:website": customWebsite,
      },
    });
    const {
      user: { username },
    } = signUpResult as any as SignUpUsername;
    return username;
  };
};

export const useResendSignUp = () => {
  return async function resendSignUp(email: string): Promise<void> {
    await Auth.resendSignUp(email);
  };
};

export const useForgotPassword = () => {
  return async function forgotPassword(email: string): Promise<void> {
    await Auth.forgotPassword(email);
  };
};

export const useForceResetPassword = () => {
  return async function forceResetPassword(cognitoUser: any, newPassword: string): Promise<void> {
    await Auth.completeNewPassword(cognitoUser, newPassword);
  };
};

export const useResetPassword = () => {
  return async function resetPassword({
    email,
    code,
    password,
  }: ResetPasswordInput): Promise<void> {
    await Auth.forgotPasswordSubmit(email, code, password);
  };
};
